import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthGuard, AuthService } from '@auth0/auth0-angular';
import { Observable, take, tap } from 'rxjs';
import { PartnerLoginService } from '../services/partner-login.service';

/**
 * Provided in place of Auth0's AuthGuard (see AuthenticationModule), so every route that lists AuthGuard uses this.
 *
 * Auth0's guard starts its own loginWithRedirect for logged-out users, which goes to the standard Auth0 login.
 * In partner portal environments that races AuthState's redirect to the partner portal and usually wins, so here
 * the guard sends the user to the portal itself, saving the URL they were navigating to. When the portal hands the
 * user back with a login hint, AuthState starts the partner login, so the guard stays out of the way. Everywhere
 * else this is Auth0's own behavior.
 */
@Injectable()
export class PartnerAuthGuard extends AuthGuard {
	constructor(
		private authService: AuthService,
		private partnerLoginService: PartnerLoginService
	) {
		super(authService);
	}

	canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		if (this.partnerLoginService.shouldRedirectToPartnerPortal() || this.hasPartnerLoginHint()) {
			return this.handlePartnerLogin(state);
		}
		return super.canActivate(next, state);
	}

	canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		if (this.partnerLoginService.shouldRedirectToPartnerPortal() || this.hasPartnerLoginHint()) {
			return this.handlePartnerLogin(state);
		}
		return super.canActivateChild(childRoute, state);
	}

	private handlePartnerLogin(state: RouterStateSnapshot): Observable<boolean> {
		return this.authService.isAuthenticated$.pipe(
			take(1),
			tap((authenticated: boolean) => {
				if (!authenticated && !this.hasPartnerLoginHint()) {
					this.partnerLoginService.redirectToPartnerPortal(state.url);
				}
			})
		);
	}

	// AuthState reads the hint the same way, so the two always agree on whether a partner login is in progress
	private hasPartnerLoginHint(): boolean {
		return this.partnerLoginService.isPartnerLoginEnabled() && !!this.partnerLoginService.getLoginHint();
	}
}
